import { SortDirection } from '@angular/material/sort';
import { MatMultiSortTableDataSource } from '@dice/app/components/organisms/mat-multi-sort/mat-multi-sort-data-source';
import { MatMultiSortHeaderComponent } from '@dice/app/components/organisms/mat-multi-sort/mat-multi-sort-header/mat-multi-sort-header.component';
import { Settings } from '@dice/app/components/organisms/mat-multi-sort/utils';
import { BehaviorSubject, Subject } from 'rxjs';

export class TableData<T> {
  private _dataSource: MatMultiSortTableDataSource<T>;

  private readonly _columns: BehaviorSubject<{ id: string; name?: string; isActive?: boolean }[]>;

  private readonly _displayedColumns: string[];

  private _sortParams: string[];

  private _sortDirs: SortDirection[];

  private readonly _key: string;

  private _sortObservable: Subject<void> = new Subject<void>();

  constructor(
    columns: { id: string; name?: string; isActive?: boolean }[],
    options?: {
      localStorageKey?: string;
    },
  ) {
    this._columns = new BehaviorSubject(
      columns.map((c) => {
        if (c.isActive === undefined) {
          c.isActive = true;
        }
        return c;
      }),
    );
    this._displayedColumns = this._columns.value.filter((c) => c.isActive).map((c) => c.id);

    if (options) {
      this._key = options.localStorageKey;
    } else {
      this._sortParams = [];
      this._sortDirs = [];
    }
    this.init();
  }

  public onSortEvent(): void {
    this._sortParams = this._dataSource._multiSort.actives;
    this._sortDirs = this._dataSource._multiSort.directions;
    this.orderData();
    this._sortObservable.next();
    this.storeTableSettings();
  }

  private init(): void {
    if (this._key) {
      const settings = new Settings(this._key);
      settings.load();
      if (this.isLocalStorageSettingsValid(settings)) {
        this.columns = settings.columns;
        this._sortDirs = settings.sortDirs;
        this._sortParams = settings.sortParams;
      } else {
        this._sortDirs = [];
        this._sortParams = [];
      }
    }
  }

  orderData(): void {
    this._dataSource.orderData();
  }

  private isLocalStorageSettingsValid(settings: Settings): boolean {
    // check if number of columns matching
    if (settings.columns.length !== this._columns.value.length) {
      return false;
    }

    // check if columns are the same
    for (const column of settings.columns) {
      const match = this._columns.value.filter((c) => c.id === column.id && c.name === column.name);
      if (match === undefined) {
        return false;
      }
    }
    return true;
  }

  public storeTableSettings(): void {
    if (this._key) {
      const settings: Settings = new Settings(this._key);
      settings.columns = this._columns.value;
      settings.sortParams = this._sortParams;
      settings.sortDirs = this._sortDirs;
      settings.save();
    }
  }

  public get displayedColumns(): string[] {
    return this._displayedColumns;
  }

  public set dataSource(dataSource: MatMultiSortTableDataSource<T>) {
    this._dataSource = dataSource;
  }

  public get dataSource(): MatMultiSortTableDataSource<T> {
    return this._dataSource;
  }

  public set data(data: T[]) {
    this._dataSource.data = data;
    this.orderData();
  }

  public set columns(v: { id: string; name?: string; isActive?: boolean }[]) {
    this._columns.next(
      v.map((c) => {
        if (c.isActive === undefined) {
          c.isActive = true;
        }
        return c;
      }),
    );
  }

  public get columns(): { id: string; name?: string; isActive?: boolean }[] {
    return this._columns.value;
  }

  public get sortObservable(): Subject<any> {
    return this._sortObservable;
  }

  public get sortParams(): string[] {
    return this._sortParams;
  }

  public get sortDirs(): SortDirection[] {
    return this._sortDirs;
  }

  initialize(): void {
    if (!this._dataSource._multiSort) return;

    this._dataSource._multiSort.actives = this.sortParams;
    this._dataSource._multiSort.directions = this.sortDirs;

    // workaround to trigger change detection in the sort header, so the sort indicators (arrows) will be rendered on the first page load ...
    const actives: string[] = this._dataSource._multiSort.actives;
    if (actives?.length > 0) {
      actives.forEach((active: string) => {
        const sortedSortable: MatMultiSortHeaderComponent = this._dataSource._multiSort.sortables.get(
          active,
        ) as MatMultiSortHeaderComponent;
        if (sortedSortable && sortedSortable.refreshArrow) {
          sortedSortable.refreshArrow();
        }
      });
    }

    this.orderData();
  }
}
